import { CircularProgress } from "@material-ui/core";
import { OrgWorker } from "model/OrganizationalStructure";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useOrganizationalStructureStore } from "web-apps/company/stores/organizationalStructureStore/organizationalStructureStore";
import { ButtonComponent } from "../general/button/button.component";
import { InfoModal } from "../general/infoModal/infoModal.component";
import { Pagination } from "../general/pagination.component";
import { ChooseWorkersModal } from "./chooseWorkersModal.component";
import type { Worker as ChooseWorkersModalWorker } from "./chooseWorkersModal.types";
import { EmptyStructure } from "./emptyStructure.component";
import { StructureDropDownRow } from "./structureDropDownRow.component";
import { WorkerRow } from "./workerRow.component";

type Props = {
  workgroup: any;
  isOpen: boolean;
  members: OrgWorker[];
  pagination: { currentPage: number; totalPages: number };
  isLoading: boolean;
  onToggle: () => void;
  onPageChange: (page: number) => void;
  onWorkerClick: React.Dispatch<React.SetStateAction<number | undefined>>;
  allMembers: OrgWorker[];
  searchText?: string;
  memberCount: number;
};

export const TeamRow: React.FC<Props> = (props) => {
  const [showInfo, setShowInfo] = useState(false);
  const [error, setError] = useState(false);
  const [showAssignInfo, setShowAssignInfo] = useState(false);
  const [assignError, setAssignError] = useState(false);
  const { t } = useTranslation();
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [companyState] = useCompanyStore();
  const [, organizationalStructureActions] = useOrganizationalStructureStore();

  const closeModal = () => {
    setShowInfo(false);
    setError(false);
  };

  const closeAssignModal = () => {
    setShowAssignInfo(false);
    setAssignError(false);
  };

  const assignWorkers = async (checked: ChooseWorkersModalWorker[]) => {
    if (
      !companyState.company?.id ||
      checked.length === 0 ||
      !props.workgroup.id
    ) {
      return;
    }

    const existingWorkers: OrgWorker[] =
      props.workgroup.workers ?? props.members ?? [];

    const existingWorkerIds = new Set(
      existingWorkers.map((worker: OrgWorker) => worker.workerId)
    );

    const workersToAssign = checked.filter(
      (worker) => !existingWorkerIds.has(worker.workerId)
    );

    if (workersToAssign.length === 0) {
      setAssignError(false);
      setShowAssignInfo(true);
      return;
    }

    try {
      await Promise.all(
        workersToAssign.map((worker) =>
          organizationalStructureActions.assignWorkerToTeam(
            companyState.company!.id,
            props.workgroup.id!,
            worker.workerId
          )
        )
      );
      setAssignError(false);
      setShowAssignInfo(true);
      setShowAssignModal(false);
    } catch (e) {
      setAssignError(true);
      setShowAssignInfo(true);
    }
  };

  return (
    <>
      <div>
        <StructureDropDownRow
          type={"team"}
          data={props.workgroup}
          onToggle={props.onToggle}
          isOpen={props.isOpen}
          memberCount={props.memberCount}
        />
        {props.isOpen && (
          <>
            {props.isLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "20px",
                }}
              >
                <CircularProgress style={{ width: "60px", height: "60px" }} />
              </div>
            ) : (
              <>
                {props.members.length > 0 ? (
                  <>
                    <ButtonComponent
                      size={"small"}
                      onClick={() => setShowAssignModal(true)}
                      action={true}
                      marginTop={10}
                      marginBottom={0}
                      width={150}
                      alignRight
                    >
                      {t("OrganizationalStructure.AssignToTeam")}
                    </ButtonComponent>
                    {props.members.map((member) => (
                      <WorkerRow
                        marginLeft={40}
                        key={member.workerId}
                        worker={member}
                        setClickedWorker={props.onWorkerClick}
                        workgroupId={props.workgroup.id}
                        setError={setError}
                        setShowInfo={setShowInfo}
                      />
                    ))}
                    <Pagination
                      small
                      setPage={(newPageSetter) => {
                        const newPage =
                          typeof newPageSetter === "function"
                            ? newPageSetter(props.pagination.currentPage)
                            : newPageSetter;
                        props.onPageChange(newPage);
                      }}
                      page={props.pagination.currentPage}
                      totalPages={props.pagination.totalPages}
                    />
                  </>
                ) : (
                  <EmptyStructure
                    type={"Worker"}
                    id={props.workgroup.id}
                    parentId={props.workgroup.companyUnitId}
                  />
                )}
              </>
            )}
            {showAssignModal && props.workgroup.id && (
              <ChooseWorkersModal
                setShowModal={setShowAssignModal}
                showModal={showAssignModal}
                onClick={assignWorkers}
                title={t("OrganizationalStructure.AssignWorkers")}
                info={t("OrganizationalStructure.AssignWorkersInfo")}
                buttonTitle={t("OrganizationalStructure.Assign")}
                filter={"All"}
                selected={props.workgroup.workers}
              />
            )}
          </>
        )}
      </div>
      {showInfo && (
        <InfoModal
          isOpen={showInfo}
          title={
            error
              ? t("OrganizationalStructure.SomethingWentWrong")
              : t("OrganizationalStructure.UnAssigned")
          }
          message={
            error
              ? t("OrganizationalStructure.SomethingWentWrongInfo")
              : t("OrganizationalStructure.UnAssignedInfo")
          }
          confirmText={t("OrganizationalStructure.Ok")}
          onConfirm={() => closeModal()}
          onCancel={() => closeModal()}
        />
      )}
      {showAssignInfo && (
        <InfoModal
          isOpen={showAssignInfo}
          title={
            assignError
              ? t("OrganizationalStructure.SomethingWentWrong")
              : t("OrganizationalStructure.Assigned")
          }
          message={
            assignError
              ? t("OrganizationalStructure.SomethingWentWrongInfo")
              : t("OrganizationalStructure.AssignedInfo")
          }
          confirmText={t("OrganizationalStructure.Ok")}
          onConfirm={closeAssignModal}
          onCancel={closeAssignModal}
        />
      )}
    </>
  );
};
